import { routeExample, viaX } from "./example-layout";
import { shouldAutoFuse } from "./auto-fuse";
import { computeSystemCableSpec } from "./cable-spec";
import { resolveCablePolarity } from "./cable-color";
import { getDefinition } from "./catalog";
import { indexNodesById } from "./node-tree";
import { SCHEMA_VERSION } from "./schema/version";
import { DEFAULT_SOC } from "./simulate/battery-runtime";
import type { SimulateState } from "./simulate-store-slice";
import type { Cable, ComponentNode, System } from "./types";

/** A cloudy day with the controller and both loads running. */
export function heroSimulateState(): SimulateState {
  return {
    componentStates: { solar: "cloudy", mppt: "on" },
    batterySoc: { battery: DEFAULT_SOC },
    loadStates: { fridge: { active: true }, led: { active: true } },
  };
}

function comp(
  id: string,
  definitionId: string,
  x: number,
  y: number,
  extra: Partial<ComponentNode> = {},
): ComponentNode {
  return { id, kind: "component", definitionId, position: { x, y }, ...extra };
}

function cable(
  id: string,
  from: [string, string],
  to: [string, string],
  lengthM: number,
): Cable {
  return {
    id,
    fromNodeId: from[0],
    fromTerminalId: from[1],
    toNodeId: to[0],
    toTerminalId: to[1],
    lengthM,
  };
}

/**
 * Ratings come from the live sizing engine, never hand-typed. The PV runs come back
 * unfused: `shouldAutoFuse` declines 9.03 A of panel into 15 A / 32 A terminals.
 */
function fuseHeroCables(system: System): System {
  const nodesById = indexNodesById(system.nodes);
  const cables = system.cables.map((c) => {
    if (resolveCablePolarity(c, nodesById, system.cables) !== "positive") return c;
    const spec = computeSystemCableSpec(c, system, getDefinition);
    if (!shouldAutoFuse(spec)) return c;
    return { ...c, fuseAmpOverride: spec.fuse, fuseAmpAuto: true };
  });
  return { ...system, cables };
}

/**
 * Positive distribution sits before the loads; negative distribution sits
 * after them. The MPPT and loads take the same top-to-bottom stud order on both
 * bars, so none of the cable paths cross. The battery return runs below them.
 * Compact columns leave room for the cable bends without long empty runs.
 */
export function heroSystem(): System {
  const nodes: ComponentNode[] = [
    // 175 W stays under the 75/15's 220 W ceiling at 12 V; the 215 W sibling does not.
    comp("solar", "solar-victron-175", 0, 0, {
      terminalOverrides: { pos: { side: "right", offset: 0.3, pinned: true }, neg: { side: "right", offset: 0.75, pinned: true } },
    }),
    // SmartSolar installation §4.3 requires disconnection of both PV conductors.
    comp("pv-isolator", "dist-solar-dp-isolator", 235, 28),
    comp("mppt", "mppt-victron-7515", 370, 0, {
      terminalOverrides: {
        "pv-pos": { side: "left", offset: 0.3, pinned: true }, "pv-neg": { side: "left", offset: 0.75, pinned: true },
        "batt-pos": { side: "bottom", offset: 0.3, pinned: true }, "batt-neg": { side: "right", offset: 0.75, pinned: true },
      },
    }),
    // Renogy Mini Core RBT12100LFP-M has an internal BMS, including a 0 °C
    // charge cutoff (Core Mini manual pp. 52–55); no external BMS is required.
    comp("battery", "batt-renogy-lfp-100", 0, 270, {
      label: "Battery 100 Ah",
      // Drop it and the 0.8 m feeder trips `detectBatteryMissingTerminalFuse`
      // and `detectFuseTooFarFromBattery` (E-11.10's 175 mm window).
      // The example uses a 40 A MRBF. The engine keeps operating current
      // separate from protection: the feeder's 4 mm² has 45 A ampacity
      // (ABYC E-11 TABLE VI-A), sufficient for the installed terminal fuse.
      batteryFuse: { ratingA: 40, type: "MRBF" },
      terminalOverrides: { pos: { side: "right", offset: 0.3, pinned: true }, neg: { side: "bottom", offset: 0.5, pinned: true } },
    }),
    comp("posbus", "dist-pos-bus", 280, 210, { studCount: 3 }),
    comp("negbus", "dist-neg-bus", 920, 210, { studCount: 3, rotation: 180 }),
    comp("fridge", "load-fridge-dometic", 550, 140, {
      label: "Fridge",
      terminalOverrides: { pos: { side: "left", offset: 0.3, pinned: true }, neg: { side: "right", offset: 0.75, pinned: true } },
    }),
    comp("led", "load-led-strip", 550, 290, {
      terminalOverrides: { pos: { side: "left", offset: 0.3, pinned: true }, neg: { side: "right", offset: 0.75, pinned: true } },
    }),
  ];

  const cables: Cable[] = [
    cable("h1", ["solar", "pos"], ["pv-isolator", "in-pos"], 2.5),
    cable("h2", ["solar", "neg"], ["pv-isolator", "in-neg"], 2.5),
    cable("h11", ["pv-isolator", "out-pos"], ["mppt", "pv-pos"], 0.5),
    cable("h12", ["pv-isolator", "out-neg"], ["mppt", "pv-neg"], 0.5),
    cable("h3", ["mppt", "batt-pos"], ["posbus", "b"], 1.2),
    cable("h4", ["mppt", "batt-neg"], ["negbus", "d"], 1.2),
    cable("h5", ["battery", "pos"], ["posbus", "a"], 0.8),
    cable("h6", ["battery", "neg"], ["negbus", "a"], 0.8),
    cable("h7", ["posbus", "c"], ["fridge", "pos"], 2.0),
    cable("h8", ["fridge", "neg"], ["negbus", "c"], 2.0),
    cable("h9", ["posbus", "d"], ["led", "pos"], 3.5),
    cable("h10", ["led", "neg"], ["negbus", "b"], 3.5),
  ];

  return routeExample(fuseHeroCables({
    name: "Camper van — solar, fridge and lights",
    nodes,
    cables,
    zoneDistances: {},
    version: SCHEMA_VERSION,
  }), {
    h1: viaX(215),
    h2: viaX(215),
    h11: viaX(340),
    h12: viaX(340),
    h3: (from, to) => [{ x: from.x, y: to.y }],
    h4: viaX(870),
    h5: viaX(240),
    h6: (from, to) => [{ x: from.x, y: 430 }, { x: 1000, y: 430 }, { x: 1000, y: to.y }],
    h7: viaX(460),
    h8: viaX(830),
    h9: viaX(460),
    h10: viaX(830),
  });
}
